import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ScrollSnapWheel from './ScrollSnapWheel'
import { SpecCalendarIcon } from './ListingSpecIcons'
import { USAGE_DURATION_ORDER, type UsageDurationKey, isUsageDurationKey } from '../utils/usageDuration'

const USAGE_ACCENT = '#48a0b8'

type ValueChipProps = {
  value: string
  ariaLabel?: string
}

/** Selected usage label — same slot as battery/cycles chips in builder spec cells. */
export function UsageDurationValueChip({
  value,
  ariaLabel = 'Usage duration',
}: ValueChipProps) {
  const { t } = useTranslation()
  const known = isUsageDurationKey(value)
  const tone = known ? USAGE_ACCENT : '#94a3b8'
  const selectedLabel = known ? t(`listing.usage.${value}`) : null

  return (
    <span
      className={`listing-spec-value-chip listing-spec-value-chip--battery usage-duration-value-chip${
        known ? '' : ' listing-spec-value-chip--muted usage-duration-value-chip--empty'
      }`}
      style={known ? { color: tone, borderColor: `${tone}55` } : undefined}
      aria-label={ariaLabel}
    >
      <SpecCalendarIcon size={18} />
      {selectedLabel ? (
        <span className="usage-duration-value-chip__text">{selectedLabel}</span>
      ) : null}
    </span>
  )
}

interface UsageDurationWheelPickerProps {
  value: string
  onChange: (value: UsageDurationKey) => void
  compact?: boolean
  ariaLabel?: string
  showValueChip?: boolean
}

export default function UsageDurationWheelPicker({
  value,
  onChange,
  compact = false,
  ariaLabel,
  showValueChip = true,
}: UsageDurationWheelPickerProps) {
  const { t } = useTranslation()
  const label = ariaLabel ?? t('listing.builder.usageDurationTitle')

  const items = USAGE_DURATION_ORDER
  const known = isUsageDurationKey(value)
  const wheelValue: UsageDurationKey = known ? value : items[0]

  const formatItem = useMemo(
    () => (key: UsageDurationKey) => t(`listing.usage.${key}`),
    [t],
  )

  return (
    <div className={`battery-scroll-field usage-duration-scroll-field${compact ? ' battery-scroll-field--compact' : ''}`}>
      {showValueChip ? (
        <div className="listing-spec-cell__value usage-duration-chip-wrap">
          <UsageDurationValueChip value={value} ariaLabel={label} />
        </div>
      ) : null}
      <ScrollSnapWheel
        className="usage-duration-scroll-wheel"
        value={wheelValue}
        onChange={(key) => onChange(key)}
        items={items}
        ariaLabel={label}
        formatItem={formatItem}
        highlightActive={known}
        compact={compact}
      />
    </div>
  )
}
